/**
 * Catálogo de campos específicos por tipo de equipo.
 *
 * Los "campos comunes" (tipo_equipo, ubicación/zona, marca, modelo, nº serie,
 * año fabricación, fecha instalación, observaciones, estado) los pinta SIEMPRE
 * el formulario y se guardan en columnas nativas de `elementos`. Aquí solo
 * declaramos los DATOS TÉCNICOS específicos del tipo, que viven en la columna
 * JSON `elementos.datos_tecnicos`.
 */

export type FieldType =
  | 'text'
  | 'number'
  | 'date'
  | 'monthyear'
  | 'select'
  | 'boolean'
  | 'textarea';

export interface FieldDef {
  key: string;
  label: string;
  type: FieldType;
  options?: string[];
  placeholder?: string;
  help?: string;
  /** Si está presente, este campo se calcula automáticamente a partir de otros. */
  derived?: {
    from: string;
    /** Años a sumar al valor `from`. */
    addYears: number;
  };
  /** Agrupa visualmente con otros campos del mismo `group` en una sola tarjeta (valor + unidad). */
  group?: string;
  /** Etiqueta opcional para la tarjeta de grupo (se toma del primer campo si no se indica). */
  groupLabel?: string;
}

export interface TypeSchema {
  /** Etiquetas que coinciden con este tipo (case + acentos insensibles). */
  match: string[];
  /** Campos específicos. */
  fields: FieldDef[];
}

// ---------- presets reutilizables ----------

const F = {
  numZonas: { key: 'numero_zonas', label: 'Nº de zonas', type: 'number' } as FieldDef,
  numParticiones: { key: 'numero_particiones', label: 'Nº de particiones', type: 'number' } as FieldDef,
  numLazos: { key: 'numero_lazos', label: 'Nº de lazos', type: 'number' } as FieldDef,
  capLazo: { key: 'capacidad_lazo', label: 'Capacidad por lazo', type: 'number' } as FieldDef,
  firmware: { key: 'firmware', label: 'Versión firmware', type: 'text' } as FieldDef,
  ip: { key: 'ip', label: 'IP', type: 'text' } as FieldDef,
  usuario: { key: 'usuario', label: 'Usuario', type: 'text' } as FieldDef,
  contrasena: { key: 'contrasena', label: 'Contraseña', type: 'text' } as FieldDef,
  codigoInstalador: { key: 'codigo_instalador', label: 'Código instalador', type: 'text' } as FieldDef,
  comunicador: {
    key: 'tipo_comunicador',
    label: 'Tipo de comunicador',
    type: 'select',
    options: ['GSM', 'IP', 'PSTN', 'GSM/IP', 'No tiene'],
  } as FieldDef,
  bateriasCant: { key: 'baterias_cantidad', label: 'Baterías · cantidad', type: 'number' } as FieldDef,
  bateriasV: { key: 'baterias_voltaje', label: 'Baterías · V', type: 'number' } as FieldDef,
  bateriasAh: { key: 'baterias_ah', label: 'Baterías · Ah', type: 'number' } as FieldDef,
  bateriasFecha: { key: 'baterias_fecha', label: 'Baterías · fecha instalación', type: 'monthyear' } as FieldDef,
  vrCableado: {
    key: 'vr_cableado',
    label: 'VR / Cableado',
    type: 'select',
    options: ['Cableado', 'Vía radio'],
  } as FieldDef,
  zona: { key: 'zona_asociada', label: 'Zona asociada', type: 'text' } as FieldDef,
  particion: { key: 'particion', label: 'Partición', type: 'text' } as FieldDef,
  lazo: { key: 'lazo', label: 'Lazo', type: 'number' } as FieldDef,
  direccion: { key: 'direccion', label: 'Dirección', type: 'text' } as FieldDef,
  p2p: { key: 'p2p', label: 'P2P', type: 'text' } as FieldDef,
  ddns: { key: 'ddns', label: 'DDNS', type: 'text' } as FieldDef,
  supervisada: { key: 'supervisada', label: 'Supervisada', type: 'boolean' } as FieldDef,
};

const fuenteAlimentacion: FieldDef[] = [
  F.bateriasCant, F.bateriasV, F.bateriasAh, F.bateriasFecha, F.supervisada,
];

const bateriasBlock: FieldDef[] = [
  F.bateriasCant, F.bateriasV, F.bateriasAh, F.bateriasFecha,
];

const retimbradoExtintor: FieldDef[] = [
  { key: 'agente', label: 'Agente extintor', type: 'select', options: ['Polvo ABC', 'CO2', 'Agua', 'Espuma', 'Clase K', 'Otro'] },
  { key: 'capacidad', label: 'Capacidad (kg/l)', type: 'number' },
  { key: 'peso', label: 'Peso (kg)', type: 'number' },
  { key: 'eficacia', label: 'Eficacia', type: 'text' },
  { key: 'fecha_ultimo_retimbre', label: 'Último retimbre', type: 'monthyear' },
  {
    key: 'fecha_proximo_retimbre',
    label: 'Próximo retimbre',
    type: 'monthyear',
    derived: { from: 'fecha_ultimo_retimbre', addYears: 5 },
  },
  { key: 'fecha_fabricacion', label: 'Fecha fabricación', type: 'monthyear' },
  {
    key: 'fecha_caducidad',
    label: 'Caducidad',
    type: 'monthyear',
    derived: { from: 'fecha_fabricacion', addYears: 20 },
  },
];

// Unidades soportadas en placa de fabricante (espejan src/lib/checklists/units.ts).
const UNIDAD_CAUDAL_OPTS = ['l/min', 'm³/h', 'l/s', 'gpm'];
const UNIDAD_PRESION_OPTS = ['bar', 'psi', 'mca', 'kg/cm²', 'kPa', 'MPa'];

const motorBomba: FieldDef[] = [
  { key: 'motor_potencia', label: 'Motor · potencia (kW)', type: 'number' },
  { key: 'motor_tension', label: 'Motor · tensión (V)', type: 'number' },
  { key: 'motor_rpm', label: 'Motor · RPM', type: 'number' },
  // Caudal nominal (placa) + unidad — pareja visual
  { key: 'bomba_caudal', label: 'Caudal nominal', type: 'number', group: 'nominal-caudal', groupLabel: 'Caudal nominal (placa)' },
  { key: 'bomba_caudal_unidad', label: 'Unidad', type: 'select', options: UNIDAD_CAUDAL_OPTS, group: 'nominal-caudal' },
  // Presión nominal (placa) + unidad — pareja visual.
  // `bomba_altura` se mantiene como clave por compatibilidad con datos existentes.
  { key: 'bomba_altura', label: 'Presión nominal (altura)', type: 'number', group: 'nominal-presion', groupLabel: 'Presión nominal (placa)' },
  { key: 'presion_unidad', label: 'Unidad', type: 'select', options: UNIDAD_PRESION_OPTS, group: 'nominal-presion' },
  // Presión de arranque — no es dato de placa; se mide con el manómetro de impulsión.
  { key: 'presion_arranque', label: 'Presión de arranque', type: 'number', help: 'En la unidad del manómetro de impulsión configurado en el sistema.' },
];

// ---------- catálogo ----------

export const TYPE_SCHEMAS: TypeSchema[] = [
  // ===== INTRUSIÓN =====
  // Nota: marca, modelo, mac_serie, usuario, contrasena, codigo_instalador,
  // vr, zona y area_particion son COLUMNAS NATIVAS de `elementos`; aquí
  // solo van campos técnicos específicos que viven en `datos_tecnicos`.
  {
    match: ['central intrusion', 'central de intrusion', 'panel intrusion', 'central'],
    fields: [F.numZonas, F.numParticiones, ...bateriasBlock, F.firmware, F.ip, F.comunicador, F.usuario, F.contrasena, F.codigoInstalador],
  },
  {
    match: ['teclado'],
    fields: [
      { key: 'direccion_bus', label: 'Dirección bus', type: 'text' },
      F.vrCableado, F.zona, F.particion,
    ],
  },
  {
    match: ['detector volumetrico', 'pir', 'detector dual', 'detector cortina', 'volumetrico'],
    fields: [
      { key: 'tecnologia', label: 'Tecnología', type: 'select', options: ['PIR', 'DT', 'MW', 'Cortina'] },
      { key: 'alcance_m', label: 'Alcance (m)', type: 'number' },
      { key: 'antimasking', label: 'Antimasking', type: 'boolean' },
      F.vrCableado, F.zona, F.particion,
    ],
  },
  {
    match: ['contacto magnetico', 'magnetico'],
    fields: [
      { key: 'tipo_contacto', label: 'Tipo', type: 'select', options: ['Puerta', 'Ventana', 'Persiana', 'Otro'] },
      F.vrCableado, F.zona, F.particion,
    ],
  },
  {
    match: ['sirena intrusion', 'sirena interior', 'sirena exterior', 'sirena'],
    fields: [
      { key: 'sirena_tipo', label: 'Tipo', type: 'select', options: ['Interior', 'Exterior'] },
      F.vrCableado, F.zona, F.particion,
    ],
  },
  {
    match: ['modulo expansor', 'expansor', 'modulo de expansion'],
    fields: [
      { key: 'expansor_tipo', label: 'Tipo', type: 'select', options: ['Zonas', 'Relés', 'Mixto'] },
    ],
  },
  {
    match: ['fuente de alimentacion', 'fuente alimentacion', 'fuente'],
    fields: [
      ...bateriasBlock,
      { key: 'supervisada', label: 'Supervisada', type: 'boolean' },
    ],
  },




  // ===== CCTV =====
  {
    match: ['nvr', 'dvr', 'grabador'],
    fields: [
      { key: 'num_canales', label: 'Nº canales', type: 'number' },
      { key: 'capacidad_disco_tb', label: 'Capacidad disco (TB)', type: 'number' },
      F.ip, F.usuario, F.contrasena, F.p2p, F.ddns,
    ],
  },
  {
    match: ['camara', 'camara cctv'],
    fields: [
      { key: 'camara_tipo', label: 'Tipo', type: 'select', options: ['Bullet', 'Domo', 'PTZ', 'Fisheye', 'Otro'] },
      F.ip,
      { key: 'canal_grabador', label: 'Canal NVR/DVR', type: 'number' },
    ],
  },
  {
    match: ['servidor analitica', 'servidor de analitica', 'analitica'],
    fields: [
      { key: 'num_canales', label: 'Nº canales', type: 'number' },
      F.ip, F.usuario, F.contrasena, F.p2p, F.ddns,
    ],
  },

  // ===== CONTROL DE ACCESOS =====
  {
    match: ['lector control accesos', 'control accesos', 'lector accesos', 'lector'],
    fields: [
      F.p2p, F.ip, F.usuario, F.contrasena,
      { key: 'tecnologia', label: 'Tecnología', type: 'select', options: ['RFID', 'Biométrico', 'Teclado', 'Mixto'] },
      { key: 'puerta_asociada', label: 'Puerta asociada', type: 'text' },
      { key: 'controlador', label: 'Controlador', type: 'text' },
    ],
  },

  // ===== DETECCIÓN INCENDIOS =====
  {
    match: ['central deteccion', 'central incendios', 'central de deteccion'],
    fields: [F.numLazos, F.capLazo, F.firmware, ...bateriasBlock],
  },
  {
    match: ['detector optico', 'detector humo', 'detector termico', 'detector multicriterio', 'detector incendio'],
    fields: [
      { key: 'detector_tipo', label: 'Tipo detector', type: 'select', options: ['Óptico', 'Térmico', 'Multicriterio'] },
      F.lazo, F.direccion, F.zona,
      { key: 'temperatura_disparo', label: 'Temperatura disparo (°C)', type: 'number' },
    ],
  },
  {
    match: ['detector aspiracion', 'dha', 'vesda', 'hssd'],
    fields: [
      { key: 'num_canales', label: 'Nº canales', type: 'number' },
      { key: 'longitud_tuberia_m', label: 'Longitud tubería (m)', type: 'number' },
      { key: 'num_orificios', label: 'Nº de orificios', type: 'number' },
      { key: 'area_cubierta_m2', label: 'Área cubierta (m²)', type: 'number' },
      { key: 'sensibilidad', label: 'Sensibilidad (%/m)', type: 'number' },
    ],
  },
  {
    match: ['detector lineal', 'barrera humo', 'haz'],
    fields: [
      { key: 'alcance_m', label: 'Alcance (m)', type: 'number' },
      { key: 'altura_instalacion_m', label: 'Altura instalación (m)', type: 'number' },
    ],
  },
  {
    match: ['pulsador'],
    fields: [F.lazo, F.direccion, F.zona, { key: 'rearmable', label: 'Rearmable', type: 'boolean' }],
  },
  {
    match: ['sirena incendio', 'sirena flash', 'sirena-flash', 'sirena pci'],
    fields: [F.lazo, F.direccion, { key: 'flash', label: 'Con flash', type: 'boolean' }, { key: 'db_a', label: 'dB(A) a 1 m', type: 'number' }],
  },
  {
    match: ['modulo entrada', 'modulo salida', 'modulo e/s', 'modulo'],
    fields: [F.lazo, F.direccion, { key: 'funcion', label: 'Función', type: 'text' }],
  },
  {
    match: ['fuente alimentacion', 'fuente de alimentacion', 'fuente'],
    fields: fuenteAlimentacion,
  },

  // ===== CO / GASES =====
  {
    match: ['central deteccion co', 'central co', 'central monoxido', 'central de monoxido'],
    fields: [F.numZonas, F.firmware, ...bateriasBlock],
  },
  {
    match: ['detector co', 'detector monoxido', 'detector de co', 'detector de monoxido'],
    fields: [F.lazo, F.direccion, F.zona],
  },
  {
    match: ['central deteccion gases', 'central gases', 'central de gases', 'central deteccion de gases'],
    fields: [F.numZonas, F.firmware, ...bateriasBlock],
  },
  {
    match: ['detector gas', 'detector de gas'],
    fields: [F.lazo, F.direccion, F.zona, { key: 'gas_tipo', label: 'Tipo de gas', type: 'text' }],
  },
  {
    match: ['motor extraccion', 'motor de extraccion', 'extractor'],
    fields: [
      { key: 'potencia_kw', label: 'Potencia (kW)', type: 'number' },
      { key: 'tension', label: 'Tensión (V)', type: 'number' },
    ],
  },

  // ===== EXTINTORES =====
  {
    match: ['extintor'],
    fields: [{ key: 'planta', label: 'Planta', type: 'text' }, ...retimbradoExtintor],
  },

  // ===== BIE / HIDRANTES =====
  {
    match: ['bie'],
    fields: [
      { key: 'planta', label: 'Planta', type: 'text' },
      { key: 'diametro_mm', label: 'Diámetro (mm)', type: 'select', options: ['25', '45'] },
      { key: 'longitud_manguera_m', label: 'Longitud manguera (m)', type: 'number' },
      { key: 'presion_estatica_bar', label: 'Presión estática (bar)', type: 'number' },
      { key: 'fecha_ultimo_retimbre', label: 'Último retimbre', type: 'monthyear' },
      { key: 'fecha_proximo_retimbre', label: 'Próximo retimbre', type: 'monthyear', derived: { from: 'fecha_ultimo_retimbre', addYears: 5 } },
      { key: 'fecha_fabricacion', label: 'Fecha fabricación', type: 'monthyear' },
      { key: 'fecha_caducidad', label: 'Caducidad', type: 'monthyear', derived: { from: 'fecha_fabricacion', addYears: 20 } },
    ],
  },
  {
    match: ['hidrante'],
    fields: [
      { key: 'planta', label: 'Planta', type: 'text' },
      { key: 'hidrante_tipo', label: 'Tipo', type: 'select', options: ['Columna seca', 'Columna húmeda', 'Arqueta'] },
      { key: 'diametro_salida_mm', label: 'Diámetro salida (mm)', type: 'select', options: ['70', '100'] },
      { key: 'fecha_cambio_juntas', label: 'Cambio de juntas', type: 'monthyear' },
    ],
  },

  // ===== EXUTORIOS / SOBREPRESIÓN =====
  {
    match: ['botellin', 'botellin impulsion', 'botellín de impulsión'],
    fields: [
      { key: 'planta', label: 'Planta', type: 'text' },
      { key: 'capacidad', label: 'Capacidad (kg/l)', type: 'number' },
      { key: 'peso', label: 'Peso (kg)', type: 'number' },
      { key: 'eficacia', label: 'Eficacia', type: 'text' },
      { key: 'fecha_ultimo_retimbre', label: 'Último retimbre', type: 'monthyear' },
      { key: 'fecha_proximo_retimbre', label: 'Próximo retimbre', type: 'monthyear', derived: { from: 'fecha_ultimo_retimbre', addYears: 5 } },
      { key: 'fecha_fabricacion', label: 'Fecha fabricación', type: 'monthyear' },
      { key: 'fecha_caducidad', label: 'Caducidad', type: 'monthyear', derived: { from: 'fecha_fabricacion', addYears: 20 } },
    ],
  },
  {
    match: ['compresor', 'compresor sobrepresion', 'compresor sobrepresión'],
    fields: [
      { key: 'tension', label: 'Tensión (V)', type: 'number' },
      { key: 'potencia_kw', label: 'Potencia (kW)', type: 'number' },
      { key: 'rpm', label: 'RPM', type: 'number' },
      { key: 'caudal', label: 'Caudal', type: 'text' },
    ],
  },
  {
    match: ['ventilador sobrepresion', 'ventilador de sobrepresion', 'ventilador sobrepresión'],
    fields: [
      { key: 'caudal_m3h', label: 'Caudal (m³/h)', type: 'number' },
      { key: 'presion_pa', label: 'Presión (Pa)', type: 'number' },
      { key: 'potencia_kw', label: 'Potencia (kW)', type: 'number' },
      { key: 'tension', label: 'Tensión (V)', type: 'number' },
      { key: 'clase_fuego', label: 'Clase fuego (F 400ºC/2h, etc.)', type: 'text' },
    ],
  },
  {
    match: ['compuerta sobrepresion', 'compuerta de sobrepresion', 'compuerta sobrepresión'],
    fields: [
      { key: 'dimensiones_mm', label: 'Dimensiones (mm)', type: 'text' },
      { key: 'presion_consigna_pa', label: 'Presión consigna (Pa)', type: 'number' },
      { key: 'actuador', label: 'Actuador (24V / 230V)', type: 'select', options: ['24 V', '230 V', 'Mecánico'] },
    ],
  },
  {
    match: ['sensor presion diferencial', 'sensor de presion diferencial', 'sensor presión diferencial'],
    fields: [
      { key: 'rango_pa', label: 'Rango (Pa)', type: 'text' },
      { key: 'salida', label: 'Salida', type: 'select', options: ['0-10 V', '4-20 mA', 'Modbus', 'Otro'] },
      { key: 'ubicacion_tomas', label: 'Ubicación tomas', type: 'text' },
    ],
  },
  {
    match: ['central sobrepresion', 'central de sobrepresion', 'central control sobrepresion', 'central de control de sobrepresion'],
    fields: [F.firmware, ...bateriasBlock, { key: 'tension', label: 'Tensión (V)', type: 'number' }],
  },
  {
    match: ['sonda aire', 'sonda lluvia', 'sonda aire y lluvia'],
    fields: [],
  },

  // ===== TUBERÍA HÚMEDA / SECA =====
  {
    match: ['puesto control', 'puesto de control'],
    fields: [
      { key: 'planta', label: 'Planta', type: 'text' },
      { key: 'presion_suministro_bar', label: 'Presión agua suministro (bar)', type: 'number' },
      { key: 'presion_sistema_bar', label: 'Presión agua sistema (bar)', type: 'number' },
      { key: 'diametro_mm', label: 'Diámetro (mm)', type: 'number' },
    ],
  },

  // ===== PUERTAS CORTAFUEGOS =====
  {
    match: ['puerta cortafuegos', 'puerta cf'],
    fields: [
      { key: 'planta', label: 'Planta', type: 'text' },
      { key: 'clasificacion_ei', label: 'Clasificación EI', type: 'text' },
      { key: 'anchura_mm', label: 'Anchura (mm)', type: 'number' },
      { key: 'dispositivo_cierre', label: 'Dispositivo cierre', type: 'boolean' },
      { key: 'barra_antipanico', label: 'Barra antipánico', type: 'boolean' },
      { key: 'retenedor', label: 'Retenedor', type: 'boolean' },
      { key: 'selector', label: 'Selector', type: 'boolean' },
    ],
  },

  // ===== GRUPO DE PRESIÓN =====
  {
    match: ['bomba jockey', 'jockey'],
    fields: [
      ...motorBomba,
      { key: 'presion_parada', label: 'Presión de parada', type: 'number', help: 'En la unidad del manómetro de impulsión configurado en el sistema.' },
      { key: 'contador_arranques_inicio', label: 'Contador arranques inicio', type: 'number' },
      { key: 'contador_arranques_fin', label: 'Contador arranques fin', type: 'number' },
    ],
  },
  {
    match: ['bomba electrica', 'bomba eléctrica'],
    fields: [
      ...motorBomba,
      { key: 'contador_horas_inicio', label: 'Contador horas inicio', type: 'number' },
      { key: 'contador_horas_fin', label: 'Contador horas fin', type: 'number' },
    ],
  },
  {
    match: ['bomba diesel', 'bomba diésel'],
    fields: [
      ...motorBomba,
      { key: 'contador_horas_inicio', label: 'Contador horas inicio', type: 'number' },
      { key: 'contador_horas_fin', label: 'Contador horas fin', type: 'number' },
      ...bateriasBlock,
    ],
  },
  {
    match: ['vaso expansion', 'vaso de expansion'],
    fields: [
      { key: 'fecha_fabricacion', label: 'Fecha fabricación', type: 'monthyear' },
      { key: 'capacidad_l', label: 'Capacidad (L)', type: 'number' },
      { key: 'presion_maxima_bar', label: 'Presión máxima (bar)', type: 'number' },
    ],
  },
  {
    match: ['caudalimetro', 'caudalímetro'],
    fields: [
      { key: 'caudal_tipo', label: 'Tipo', type: 'text' },
      { key: 'rango_unidad', label: 'Unidad de lectura', type: 'select', options: UNIDAD_CAUDAL_OPTS },
      { key: 'rango_min', label: 'Rango mínimo', type: 'number' },
      { key: 'rango_max', label: 'Rango máximo', type: 'number' },
      { key: 'diametro_mm', label: 'Diámetro (mm)', type: 'number' },
      { key: 'fecha_ultima_calibracion', label: 'Última calibración', type: 'monthyear' },
    ],
  },
  {
    match: ['manometro impulsion', 'manometro de impulsion', 'manómetro impulsión', 'manómetro de impulsión', 'manometro presostato', 'manómetro presostato'],
    fields: [
      { key: 'unidad_presion', label: 'Unidad de lectura', type: 'select', options: UNIDAD_PRESION_OPTS },
      { key: 'rango_min', label: 'Rango mínimo', type: 'number' },
      { key: 'rango_max', label: 'Rango máximo', type: 'number' },
      { key: 'diametro_esfera_mm', label: 'Diámetro esfera (mm)', type: 'number' },
      { key: 'fecha_ultima_calibracion', label: 'Última calibración', type: 'monthyear' },
    ],
  },
  {
    match: ['manometro aspiracion', 'manometro de aspiracion', 'manómetro aspiración', 'manómetro de aspiración'],
    fields: [
      { key: 'unidad_presion', label: 'Unidad de lectura', type: 'select', options: UNIDAD_PRESION_OPTS },
      { key: 'rango_min', label: 'Rango mínimo', type: 'number' },
      { key: 'rango_max', label: 'Rango máximo', type: 'number' },
      { key: 'diametro_esfera_mm', label: 'Diámetro esfera (mm)', type: 'number' },
      { key: 'fecha_ultima_calibracion', label: 'Última calibración', type: 'monthyear' },
    ],
  },

  // ===== EXTINCIÓN AUTOMÁTICA GASEOSA =====
  {
    match: ['cilindro', 'cilindros extincion', 'botella extincion'],
    fields: [
      { key: 'agente_extintor', label: 'Agente extintor', type: 'select', options: ['FM-200', 'Novec 1230', 'CO2', 'IG-541 (Inergen)', 'IG-100', 'Otro'] },
      { key: 'fecha_ultimo_retimbre', label: 'Último retimbre', type: 'monthyear' },
      { key: 'fecha_proximo_retimbre', label: 'Próximo retimbre', type: 'monthyear', derived: { from: 'fecha_ultimo_retimbre', addYears: 10 } },
      { key: 'fecha_fabricacion', label: 'Fecha fabricación', type: 'monthyear' },
    ],
  },

  // ===== ALUMBRADO / SEÑALIZACIÓN =====
  {
    match: ['alumbrado emergencia', 'emergencia'],
    fields: [
      { key: 'lumenes', label: 'Lúmenes', type: 'number' },
      { key: 'autonomia_h', label: 'Autonomía (h)', type: 'number' },
      { key: 'permanente', label: 'Permanente', type: 'boolean' },
    ],
  },
  {
    match: ['senalizacion', 'señalización', 'señalizacion'],
    fields: [
      { key: 'senal_tipo', label: 'Tipo', type: 'select', options: ['Salida', 'Equipo PCI', 'Prohibición', 'Obligación', 'Otro'] },
      { key: 'pictograma', label: 'Pictograma', type: 'text' },
    ],
  },
];

// ---------- helpers ----------

const normalize = (s: string) =>
  (s ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

export function getSchemaForTipo(tipoEquipo?: string | null): TypeSchema | null {
  if (!tipoEquipo) return null;
  const n = normalize(tipoEquipo);
  // Tokenizamos por límites de palabra para evitar falsos positivos
  // (p.ej. "pir" coincidiendo dentro de "as[pir]acion").
  const tokens = n.split(/[^a-z0-9]+/).filter(Boolean);
  const tokenSet = new Set(tokens);
  // Mejor coincidencia: más tokens coincidentes; en empate, match más largo;
  // en empate final, gana el último definido (suele ser el más específico).
  let best: { schema: TypeSchema; tokens: number; len: number } | null = null;
  for (const schema of TYPE_SCHEMAS) {
    for (const m of schema.match) {
      const nm = normalize(m);
      const mTokens = nm.split(/[^a-z0-9]+/).filter(Boolean);
      if (mTokens.length === 0) continue;
      const allPresent = mTokens.every((t) => tokenSet.has(t));
      if (!allPresent) continue;
      if (
        !best ||
        mTokens.length > best.tokens ||
        (mTokens.length === best.tokens && nm.length > best.len) ||
        (mTokens.length === best.tokens && nm.length === best.len)
      ) {
        best = { schema, tokens: mTokens.length, len: nm.length };
      }
    }
  }
  return best?.schema ?? null;
}

/** Devuelve la lista de FieldDef del tipo (vacío si no hay esquema). */
export function getFieldsForTipo(tipoEquipo?: string | null): FieldDef[] {
  return getSchemaForTipo(tipoEquipo)?.fields ?? [];
}

/** Calcula los campos derivados (fechas) sobre un objeto datos_tecnicos. */
export function applyDerived(
  fields: FieldDef[],
  data: Record<string, any>,
): Record<string, any> {
  const out = { ...data };
  for (const f of fields) {
    if (!f.derived) continue;
    const base = out[f.derived.from];
    if (!base || typeof base !== 'string') {
      out[f.key] = '';
      continue;
    }
    // Soporta MM/AAAA o AAAA-MM (input date sin día).
    const m = base.match(/^(\d{2})\/(\d{4})$/) || base.match(/^(\d{4})-(\d{2})/);
    if (!m) continue;
    let mm: number;
    let yyyy: number;
    if (base.includes('/')) {
      mm = parseInt(m[1], 10);
      yyyy = parseInt(m[2], 10);
    } else {
      yyyy = parseInt(m[1], 10);
      mm = parseInt(m[2], 10);
    }
    yyyy += f.derived.addYears;
    out[f.key] = `${String(mm).padStart(2, '0')}/${yyyy}`;
  }
  return out;
}
